"use client";

import { API_ENDPOINT } from "@/constants/appConstants";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import { Button, Logo, PillSelector } from "@monsterindia/global-ui-components";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { PauseBenefits } from "./components/PauseBenefits";
import { UnsubscribeHeader } from "./components/UnsubscribeHeader";
import { UnsubscribeWarning } from "./components/UnsubscribeWarning";
import "./index.css";
import { unSubscribeOutReachAPI } from "./query";
import {
  OutreachSubscriptionState,
  PauseDuration,
  PillSelectorOption,
  UnsubscribeFormData,
  UnsubscribeOption,
} from "./types/unsubscribe";
import {
  PAUSE_BENEFITS,
  PAUSE_DURATION_OPTIONS,
  getConfirmButtonModifier,
  getPillSelectorOptions,
  getSingleRecruiterDescription,
  shouldShowBenefits,
  shouldShowWarning,
} from "./utils/unsubscribeUtils";

const UNSUBSCRIBE_OPTIONS = [
  {
    id: "single_recruiter",
    label: "Unsubscribe from {recruiterName}",
    description: (recruiterName: string) =>
      getSingleRecruiterDescription(recruiterName),
    eventName: "Recruiter3M",
  },
  {
    id: "all_recruiters",
    label: "Unsubscribe from all recruiters",
    description: (selectedDuration: string) =>
      PAUSE_DURATION_OPTIONS.find((opt) => opt.value === selectedDuration)
        ?.description || "",
    eventName: (selectedDuration: string) =>
      PAUSE_DURATION_OPTIONS.find((opt) => opt.value === selectedDuration)
        ?.eventName || "",
  },
];

interface UnsubscribePageProps {
  recruiterName: string;
  corpId: string;
  unSubscriptionDuration: string;
  isMobile?: boolean;
  type?: string;
  setOutreachSubscriptionData?: React.Dispatch<
    React.SetStateAction<OutreachSubscriptionState>
  >;
}

export const UnsubscribePage: React.FC<UnsubscribePageProps> = ({
  recruiterName,
  corpId,
  unSubscriptionDuration = "3",
  isMobile = false,
  type = "default",
  setOutreachSubscriptionData,
}) => {
  const router = useRouter();
  const [selectedOption, setSelectedOption] = useState<UnsubscribeOption>(
    type === "default" ? "single_recruiter" : "all_recruiters",
  );
  const [selectedDuration, setSelectedDuration] =
    useState<PauseDuration>("3_months");

  const parsedCorpId = parseInt(corpId);

  useEffect(() => {
    if (!corpId && !recruiterName) {
      router.push(`${window.location.origin}/seeker/settings`);
    }
  }, [corpId, recruiterName]);

  const onSubmit = async () => {
    const formData: UnsubscribeFormData = {
      action: "unsubscribe",
      scope: selectedOption === "all_recruiters" ? "global" : "company",
    };

    let eventProps;

    if (formData.scope === "company") {
      formData["companies"] = [
        {
          corp_id: parsedCorpId,
          company_name: recruiterName,
          duration_months: parseInt(unSubscriptionDuration),
        },
      ];
    } else {
      formData["duration_months"] = parseInt(
        selectedDuration === "permanent"
          ? "-1"
          : selectedDuration.replace("_months", ""),
      );
    }

    const response: any = await unSubscribeOutReachAPI(formData);
    const statusResponse = response.success ?? false;
    if (type === "default") {
      router.push(
        `${window.location.origin}/user?unsubscribeResponse=${statusResponse}&unsubscriptionDuration=3`,
      );
    } else if (setOutreachSubscriptionData) {
      setOutreachSubscriptionData((prevData: OutreachSubscriptionState) => ({
        ...prevData,
        unsubscribeSuccess: statusResponse,
        unsubscribeSuccessMessage: response.message || "",
        currentUnsubscribeData: {
          ...prevData?.currentUnsubscribeData,
          data: {
            ...prevData?.currentUnsubscribeData?.data,
            global_consent: {
              subscribed:
                response?.data?.subscribed ||
                prevData?.currentUnsubscribeData?.data?.global_consent
                  ?.subscribed ||
                false,
              expiry_at: response?.data.expiry_at || null,
            },
          },
        },
      }));
    }
    eventProps = {
      UnsubscriptionType:
        UNSUBSCRIBE_OPTIONS[selectedOption == "all_recruiters" ? 1 : 0]
          .eventName,
    };

    trackClevertapEvents.eventPush("Unsubscribe Recruiter Comm", eventProps);
  };

  const handleOptionChange = (option: UnsubscribeOption) => {
    setSelectedOption(option);
    if (option === "single_recruiter") {
      setSelectedDuration("3_months");
    }
  };

  const handleDurationChange = (duration: PauseDuration) => {
    setSelectedDuration(duration);
  };

  const onCancel = () => {
    if (setOutreachSubscriptionData) {
      setOutreachSubscriptionData((prevData: OutreachSubscriptionState) => ({
        ...prevData,
        unsubscribeSuccess: false,
        unsubscribeModalOpen: false,
      }));
    }
  };

  const showBenefits = shouldShowBenefits(selectedDuration);
  const showWarning = shouldShowWarning(selectedDuration);
  const buttonModifier = getConfirmButtonModifier(selectedDuration);

  const settingUnsubscribePage = (
    <div className={twMerge("")}>
      <div>
        <div className="mx-[-16px] border-b border-b-[var(--Grey-Grey-7,_#F4F4F5)] pb-4">
          <h2 className="text-pretty px-4 text-base font-bold leading-6 text-black md:pl-4">
            Are you sure you want to pause recruiter outreach?
          </h2>
        </div>

        <div
          key={UNSUBSCRIBE_OPTIONS[1].id}
          className={`flex cursor-pointer flex-col items-start gap-6 pt-4 md:gap-8 ${!isMobile ? "min-w-[634px]" : ""}`}
        >
          <div className="flex-1">
            <p className="text-sm font-normal leading-5 text-[#17142A]">
              You may miss out on job offers. Choose a duration to mute
              communication:
            </p>

            <div className="mt-4 flex">
              <PillSelector
                disabled={false}
                getValue={(selectedOptions: PillSelectorOption[]) => {
                  if (selectedOptions && selectedOptions.length > 0) {
                    handleDurationChange(
                      selectedOptions[0].id as PauseDuration,
                    );
                  }
                }}
                options={getPillSelectorOptions(selectedDuration)}
                selectionType={
                  selectedDuration === "permanent" ? "warning" : "default"
                }
              />
            </div>
          </div>

          <div className="flex w-full items-center justify-center gap-4 bg-[#FFF] py-4 pr-[4px] md:mx-[-16px] md:min-w-[666px] md:justify-end md:rounded-b-lg md:px-[20px] md:py-2 md:shadow-[0_-4px_8px_-2px_rgba(0,0,0,0.08),_0_-2px_2px_-2px_rgba(0,0,0,0.15)]">
            <Button
              className="w-full md:!w-[68px]"
              label="Cancel"
              modifier="tertiary"
              onClick={onCancel}
              size="md"
            />

            <Button
              className="w-full md:!w-[152px]"
              label="Yes, Confirm"
              modifier={buttonModifier}
              onClick={onSubmit}
              size="md"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const noActionUnsubscribePage = (
    <div
      className={twMerge(
        "min-h-screen bg-[#F6F7F9]",
        isMobile ? "pt-[16px]" : "pt-[28px]",
      )}
    >
      <div className="mb-[16px] flex w-full items-center justify-center md:mb-[28px]">
        <Logo
          alt="foundit"
          height={24}
          isMobile={false}
          logoUrl="//media.foundit.in/trex/public/theme_3/src/assets/images/header/companyLogo.svg"
          name="Vivek"
          noAvatarPages={["Account"]}
          pageName="Home"
          profileImgUrl=""
        />
      </div>

      <div className={twMerge("flex items-center justify-center")}>
        <div
          className={twMerge(
            "flex min-h-[460px] flex-col items-center justify-center gap-6 md:min-w-[800px]",
            "rounded-lg bg-white shadow-sm",
            isMobile ? "p-3" : "px-8 py-6",
          )}
        >
          <div className="text-center text-[42px] font-normal leading-[70px] text-black">
            😔
          </div>
          <div className="max-w-[512px] text-center text-base font-bold leading-6 text-[#17142A]">
            You have already unsubscribed from the recruiter.
          </div>
        </div>
      </div>
    </div>
  );

  const defaultUnsubscribePage = (
    <>
      <div className="[926px]:w-[926px] [430px]:h-[926px] absolute !z-[99] h-full w-full justify-self-center rounded-[928px] bg-[linear-gradient(90deg,rgba(255,185,71,0.50)_1.86%,rgba(112,0,255,0.44)_100.99%,rgba(112,0,255,0.44)_101%)] opacity-[0.13] blur-[100px] max-[925px]:w-full max-[429px]:h-full"></div>
      <img
        src={`${API_ENDPOINT("").media.backgroundUnsubscribeDesign}`}
        height={isMobile ? 227 : 652}
        width={isMobile ? 340 : 978}
        className="absolute bottom-[22px] right-[0px] md:bottom-[0px]"
      />
      <div
        className={twMerge(
          "min-h-screen bg-[#F6F7F9]",
          isMobile ? "pt-[16px]" : "pt-[28px]",
        )}
      >
        <div className="mb-[28px] flex w-full items-center justify-center">
          <Logo
            alt="foundit"
            height={24}
            isMobile={false}
            logoUrl="//media.foundit.in/trex/public/theme_3/src/assets/images/header/companyLogo.svg"
            name="Vivek"
            noAvatarPages={["Account"]}
            pageName="Home"
            profileImgUrl=""
          />
        </div>

        <div className="mx-4 flex items-center justify-center md:mx-0">
          <div
            className={twMerge(
              "!z-[100] rounded-lg bg-white shadow-sm",
              isMobile ? "p-3" : "px-8 py-6",
            )}
          >
            <UnsubscribeHeader />
            <UnsubscribeWarning />

            <div className="space-y-6">
              <div>
                <h2 className="mb-5 text-center text-base font-medium leading-6 text-[#17142A] md:mb-6">
                  Manage Your Communication Preferences:
                </h2>

                <div className="space-y-3">
                  {UNSUBSCRIBE_OPTIONS.map((option) => {
                    const isSelected = selectedOption === option.id;
                    const optionLabel = option.label.replace(
                      "{recruiterName}",
                      recruiterName,
                    );

                    const getDescriptionParam = () => {
                      if (option.id === "single_recruiter") {
                        return recruiterName;
                      }
                      return selectedDuration;
                    };

                    const optionDescription = isSelected
                      ? option.description(getDescriptionParam())
                      : "";

                    return (
                      <label
                        key={option.id}
                        className={`flex flex-col items-start md:flex-row ${isSelected ? "gap-3" : "gap-0"} group cursor-pointer px-3 py-4 md:px-6 md:py-3 ${!isMobile ? "min-w-[736px]" : ""} ${isSelected ? "rounded-[12px] border border-[#E8E8EB] bg-[#F6F6F7]" : "rounded-[12px] border border-[#E8E8EB] bg-[#FFF]"}`}
                      >
                        <div
                          className={`group flex w-full cursor-pointer items-start gap-3`}
                        >
                          <input
                            type="radio"
                            name="unsubscribeOption"
                            value={option.id}
                            checked={isSelected}
                            onChange={() =>
                              handleOptionChange(option.id as UnsubscribeOption)
                            }
                            className={[
                              "relative mt-[2px] h-5 w-5 appearance-none rounded-full",
                              "border-2 border-[#17142A] bg-white",
                              "transition",
                              "before:absolute before:inset-[4px] before:rounded-full before:content-['']",
                              "before:scale-0 before:bg-[#17142A] before:transition-transform before:duration-150",
                              "checked:before:scale-100",
                              "focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2",
                              "disabled:cursor-not-allowed disabled:opacity-50",
                            ].join(" ")}
                          />
                          <div className="flex-1">
                            <span className="text-base font-medium leading-6 text-[#17142A]">
                              {optionLabel}
                            </span>
                            {isSelected && optionDescription && (
                              <p
                                className={twMerge(
                                  "mt-[6px] text-sm font-normal leading-5 text-[#59566C]",
                                  option.id === "all_recruiters" && showWarning
                                    ? "text-error"
                                    : "text-darkKnight-500",
                                )}
                              >
                                {optionDescription}
                              </p>
                            )}
                            {isSelected &&
                              selectedOption === "all_recruiters" && (
                                <div className="mt-3 flex md:mt-4">
                                  <PillSelector
                                    disabled={false}
                                    getValue={(
                                      selectedOptions: PillSelectorOption[],
                                    ) => {
                                      if (
                                        selectedOptions &&
                                        selectedOptions.length > 0
                                      ) {
                                        handleDurationChange(
                                          selectedOptions[0]
                                            .id as PauseDuration,
                                        );
                                      }
                                    }}
                                    options={getPillSelectorOptions(
                                      selectedDuration,
                                    )}
                                    selectionType={
                                      selectedDuration === "permanent"
                                        ? "warning"
                                        : "default"
                                    }
                                  />
                                </div>
                              )}
                            {isSelected &&
                              selectedOption === "all_recruiters" &&
                              showBenefits && (
                                <PauseBenefits
                                  benefits={PAUSE_BENEFITS}
                                  isMobile={isMobile}
                                />
                              )}
                          </div>
                        </div>

                        <div className="w-full md:w-auto">
                          {isSelected && optionDescription && (
                            <Button
                              type="submit"
                              modifier={buttonModifier}
                              label="Confirm"
                              className={
                                isMobile ? "w-full" : "flex-1 self-center"
                              }
                              onClick={onSubmit}
                            />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {type === "settings"
        ? settingUnsubscribePage
        : type === "no-action"
          ? noActionUnsubscribePage
          : defaultUnsubscribePage}
    </>
  );
};

export default UnsubscribePage;
